import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Shield, Plus, Edit, Trash2, Crown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AdminPermissoes() {
  const [showForm, setShowForm] = useState(false);
  const [editingPerm, setEditingPerm] = useState(null);
  const [selectedMember, setSelectedMember] = useState('');
  const [formData, setFormData] = useState({
    user_email: '',
    user_name: '',
    is_super_admin: false,
    can_manage_members: false,
    can_manage_events: false,
    can_manage_groups: false,
    can_manage_communication: false,
    can_manage_gallery: false,
    can_manage_banners: false,
    can_manage_registrations: false,
    can_moderate_reviews: false,
    can_manage_church_info: false
  });

  const queryClient = useQueryClient();

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ['adminPermissions'],
    queryFn: () => base44.entities.AdminPermission.list()
  });

  const { data: members = [] } = useQuery({
    queryKey: ['activeMembers'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' })
  });
  const selectedMemberData = useMemo(
    () => members.find((m) => m.id === selectedMember),
    [members, selectedMember]
  );

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.AdminPermission.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPermissions'] });
      toast.success('Administrador adicionado!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.AdminPermission.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPermissions'] });
      toast.success('Permissões atualizadas!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.AdminPermission.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPermissions'] });
      toast.success('Administrador removido!');
    }
  });

  const resetForm = () => {
    setFormData({
      user_email: '',
      user_name: '',
      is_super_admin: false,
      can_manage_members: false,
      can_manage_events: false,
      can_manage_groups: false,
      can_manage_communication: false,
      can_manage_gallery: false,
      can_manage_banners: false,
      can_manage_registrations: false,
      can_moderate_reviews: false,
      can_manage_church_info: false
    });
    setSelectedMember('');
    setEditingPerm(null);
    setShowForm(false);
  };

  const handleMemberSelect = (memberId) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      setSelectedMember(memberId);
      setFormData({
        ...formData,
        user_email: member.email,
        user_name: member.full_name
      });
    }
  };

  const handleEdit = (perm) => {
    setEditingPerm(perm);
    setFormData({
      user_email: perm.user_email || '',
      user_name: perm.user_name || '',
      is_super_admin: perm.is_super_admin || false,
      can_manage_members: perm.can_manage_members || false,
      can_manage_events: perm.can_manage_events || false,
      can_manage_groups: perm.can_manage_groups || false,
      can_manage_communication: perm.can_manage_communication || false,
      can_manage_gallery: perm.can_manage_gallery || false,
      can_manage_banners: perm.can_manage_banners || false,
      can_manage_registrations: perm.can_manage_registrations || false,
      can_moderate_reviews: perm.can_moderate_reviews || false,
      can_manage_church_info: perm.can_manage_church_info || false
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.user_email) {
      toast.error('E-mail é obrigatório.');
      return;
    }

    if (editingPerm) {
      updateMutation.mutate({ id: editingPerm.id, data: formData });
    } else {
      createMutation.mutate({
        ...formData,
        user_id: formData.user_email // will be updated when user logs in
      });
    }
  };

  const permissionLabels = {
    can_manage_members: 'Gerenciar Membros',
    can_manage_events: 'Gerenciar Agenda',
    can_manage_groups: 'Gerenciar Grupos',
    can_manage_communication: 'Enviar Comunicados',
    can_manage_gallery: 'Gerenciar Galeria/Vídeos',
    can_manage_banners: 'Gerenciar Banners',
    can_manage_registrations: 'Gerenciar Inscrições',
    can_moderate_reviews: 'Moderar Avaliações',
    can_manage_church_info: 'Editar Informações da Igreja'
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-slate-700 to-slate-900 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Administradores" 
            subtitle="Gerenciar permissões"
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {permissions.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="Nenhum administrador"
            description="Adicione administradores para gerenciar o app."
            actionLabel="Adicionar Admin"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {permissions.map((perm, index) => (
              <motion.div
                key={perm.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    {perm.is_super_admin ? (
                      <Crown className="w-5 h-5 text-amber-500" />
                    ) : (
                      <Shield className="w-5 h-5 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {perm.user_name || perm.user_email}
                      </h3>
                      {perm.is_super_admin && (
                        <Badge className="bg-amber-100 text-amber-700">Super Admin</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{perm.user_email}</p>
                    
                    {!perm.is_super_admin && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(permissionLabels).map(([key, label]) => 
                          perm[key] && (
                            <span key={key} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {label}
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleEdit(perm)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200"
                    onClick={() => {
                      if (confirm('Remover este administrador?')) {
                        deleteMutation.mutate(perm.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPerm ? 'Editar Permissões' : 'Novo Administrador'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {!editingPerm && (
              <div>
                <Label>Selecionar Membro</Label>
                <Select value={selectedMember} onValueChange={handleMemberSelect}>
                  <SelectTrigger className="w-full">
                    {selectedMemberData ? (
                      <div className="flex items-center gap-2">
                        {selectedMemberData.photo_url ? (
                          <img
                            src={selectedMemberData.photo_url}
                            alt={selectedMemberData.full_name}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 border border-slate-200 flex items-center justify-center text-xs font-semibold">
                            {selectedMemberData.full_name?.charAt(0)?.toUpperCase() || 'M'}
                          </div>
                        )}
                        <span className="truncate">{selectedMemberData.full_name}</span>
                      </div>
                    ) : (
                      <SelectValue placeholder="Escolha um membro ativo" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {members.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        <div className="flex items-center gap-2">
                          {member.photo_url ? (
                            <img
                              src={member.photo_url}
                              alt={member.full_name}
                              className="w-6 h-6 rounded-full object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 border border-slate-200 flex items-center justify-center text-xs font-semibold">
                              {member.full_name?.charAt(0)?.toUpperCase() || 'M'}
                            </div>
                          )}
                          <span>{member.full_name}</span>
                          <span className="text-xs text-gray-500">({member.email})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>E-mail *</Label>
              <Input
                type="email"
                value={formData.user_email}
                onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                placeholder="admin@igreja.com"
                disabled={!!editingPerm}
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.user_name}
                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                placeholder="Nome do administrador"
                className="bg-gray-50"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
              <div>
                <Label className="text-amber-800">Super Admin</Label>
                <p className="text-xs text-amber-600">Acesso total a todas as funcionalidades</p>
              </div>
              <Switch
                checked={formData.is_super_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_super_admin: checked })}
              />
            </div>

            {!formData.is_super_admin && (
              <div className="space-y-3">
                <Label>Permissões Específicas</Label>
                
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b">
                    <Label className="font-normal">{label}</Label>
                    <Switch
                      checked={formData[key]}
                      onCheckedChange={(checked) => setFormData({ ...formData, [key]: checked })}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingPerm ? 'Salvar' : 'Adicionar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
